import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import PolicyStep from "./PolicyStep";
import PhoneStep from "./PhoneStep";
import VerificationStep from "./VerificationStep";
import TopBar from "../common/TopBar";
import { useAuth } from "@/hooks/useAuth";
import { isValidRedirectUrl } from "@/utils/url-validation";
import liff from "@line/liff";

/**
 * Signup Content Component
 * Handles multi-step signup flow with LIFF authentication
 * Supports redirect preservation after signup completion
 */
export default function SignupContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoggedIn } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [phone, setPhone] = useState("");
  const [registrationId, setRegistrationId] = useState("");

  // T026 & T027: Extract and validate redirect URL
  const redirectParam = searchParams.get("redirect");
  const validatedRedirectUrl =
    redirectParam && isValidRedirectUrl(redirectParam)
      ? redirectParam
      : null;

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/reload/qrcode');
    }
  }, [isLoggedIn, navigate]);

  const handlePolicyAccept = () => {
    setCurrentStep(1);
  };

  const handlePhoneSubmit = async (phoneValue: string, regId: string) => {
    setPhone(phoneValue);
    setRegistrationId(regId);
    setCurrentStep(2);
  };

  const handleVerificationSuccess = () => {
    if (validatedRedirectUrl) {
      navigate(validatedRedirectUrl);
    } else if (liff.isInClient()) {
      liff.closeWindow();
    } else {
      navigate("/");
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const stepTitles = [
    "STEP 1｜閱讀好盒器服務條款",
    "STEP 2｜輸入手機號碼",
    "STEP 3｜輸入認證碼",
  ];

  if (isLoggedIn) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <TopBar title={stepTitles[currentStep]} />

      {currentStep === 0 && (
        <PolicyStep onNext={handlePolicyAccept} onBack={() => navigate("/")} />
      )}

      {currentStep === 1 && (
        <PhoneStep
          onNext={handlePhoneSubmit}
          onBack={handleBack}
          phone={phone}
        />
      )}

      {currentStep === 2 && (
        <VerificationStep
          registrationId={registrationId}
          onNext={handleVerificationSuccess}
          onBack={handleBack}
        />
      )}
    </div>
  );
}
